// src/components/MarketInsights/components/PricingAnalytics.tsx

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { PricingStat, GroupByOption } from "../types";
import {
  formatCurrency,
  formatNumber,
  formatPricePerSqft,
} from "../utils/formatters";

interface PricingAnalyticsProps {
  data: PricingStat[] | undefined;
  isLoading: boolean;
  groupBy: GroupByOption;
  onGroupByChange: (groupBy: GroupByOption) => void;
}

type SortKey =
  | "value"
  | "count"
  | "avg_price"
  | "median_price"
  | "avg_price_per_sqft";
type SortDirection = "asc" | "desc";

const groupByOptions: { value: GroupByOption; label: string }[] = [
  { value: "neighborhood", label: "Neighborhood" },
  { value: "bedrooms", label: "Bedrooms" },
  { value: "sqft_range", label: "Sq Ft Range" },
  { value: "property", label: "Property" },
];

const PricingAnalytics: React.FC<PricingAnalyticsProps> = ({
  data,
  isLoading,
  groupBy,
  onGroupByChange,
}) => {
  const theme = useTheme();
  const [sortKey, setSortKey] = useState<SortKey>("avg_price");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton width="40%" height={32} />
          <Skeleton
            variant="rectangular"
            height={350}
            sx={{ mt: 2, borderRadius: 1 }}
          />
        </CardContent>
      </Card>
    );
  }

  // Sort data
  const sortedData = [...(data || [])].sort((a, b) => {
    const aVal = a[sortKey] ?? 0;
    const bVal = b[sortKey] ?? 0;

    if (sortKey === "value") {
      return sortDirection === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    }

    return sortDirection === "asc"
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  // Chart data (limit to top 15)
  const chartData = sortedData.slice(0, 15);

  // Color scale based on avg price
  const maxPrice = Math.max(...chartData.map((d) => d.avg_price || 0));
  const minPrice = Math.min(...chartData.map((d) => d.avg_price || 0));
  const getBarColor = (price: number | null) => {
    if (!price) return theme.palette.grey[400];
    const ratio = (price - minPrice) / (maxPrice - minPrice || 1);
    if (ratio < 0.5) {
      return alpha(theme.palette.success.main, 0.6 + ratio * 0.4);
    }
    return alpha(theme.palette.warning.main, 0.6 + (ratio - 0.5) * 0.4);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const stat = payload[0].payload as PricingStat;

    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          boxShadow: theme.shadows[4],
          minWidth: 200,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {stat.value}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Units: {formatNumber(stat.count)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Avg Price: {formatCurrency(stat.avg_price)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Median: {formatCurrency(stat.median_price)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Range: {formatCurrency(stat.min_price)} -{" "}
            {formatCurrency(stat.max_price)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Avg Sq Ft: {formatNumber(stat.avg_sqft)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            $/Sq Ft: {formatPricePerSqft(stat.avg_price_per_sqft)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Pricing Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compare pricing across different segments
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <ToggleButtonGroup
              value={groupBy}
              exclusive
              onChange={(_, value) => value && onGroupByChange(value)}
              size="small"
            >
              {groupByOptions.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              size="small"
            >
              <ToggleButton value="chart">Chart</ToggleButton>
              <ToggleButton value="table">Table</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {viewMode === "chart" ? (
          <Box sx={{ width: "100%", height: 350 }}>
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(theme.palette.divider, 0.5)}
                  horizontal={true}
                  vertical={false}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickFormatter={(value) =>
                    formatCurrency(value, { compact: true })
                  }
                  axisLine={{ stroke: theme.palette.divider }}
                />
                <YAxis
                  type="category"
                  dataKey="value"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  width={90}
                  axisLine={{ stroke: theme.palette.divider }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="avg_price" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.avg_price)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "value"}
                      direction={sortKey === "value" ? sortDirection : "asc"}
                      onClick={() => handleSort("value")}
                    >
                      {groupByOptions.find((o) => o.value === groupBy)?.label}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "count"}
                      direction={sortKey === "count" ? sortDirection : "asc"}
                      onClick={() => handleSort("count")}
                    >
                      Units
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "avg_price"}
                      direction={
                        sortKey === "avg_price" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("avg_price")}
                    >
                      Avg Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "median_price"}
                      direction={
                        sortKey === "median_price" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("median_price")}
                    >
                      Median
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Min</TableCell>
                  <TableCell align="right">Max</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortKey === "avg_price_per_sqft"}
                      direction={
                        sortKey === "avg_price_per_sqft" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("avg_price_per_sqft")}
                    >
                      $/Sq Ft
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.02
                        ),
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.value}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatNumber(row.count)}
                        size="small"
                        sx={{ minWidth: 50 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(row.avg_price)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.median_price)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.min_price)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.max_price)}
                    </TableCell>
                    <TableCell align="right">
                      {formatPricePerSqft(row.avg_price_per_sqft)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary stats */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Segments
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {data?.length || 0}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Units
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatNumber(data?.reduce((sum, d) => sum + d.count, 0))}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Overall Avg Price
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatCurrency(
                (data?.reduce(
                  (sum, d) => sum + (d.avg_price || 0) * d.count,
                  0
                ) ?? 0) / (data?.reduce((sum, d) => sum + d.count, 0) || 1)
              )}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PricingAnalytics;
